// bootstrap module must be the first one to be imported, it sets up global error handling
import './bootstrap.ts';
import './htmx.ts';

import {initDashboardRepoList} from './components/init-dashboard-repo-list.ts';

import {initDependencyInjection} from './dependencyinjection.ts';
import {initAllSpice} from './features/allspice.ts';
import {initGlobalCopyToClipboardListener} from './features/clipboard.ts';
import {initHeadwayTracking} from './features/headway.ts';
import {initContextPopups} from './features/contextpopup.ts';
import {initRepoGraphGit} from './features/repo-graph.ts';
import {initHeatmap} from './features/heatmap.ts';
import {initImageDiff} from './features/imagediff.ts';
import {initSmartReviewSeveritySlider} from './features/smart-review-severity-slider.ts';
import {initDrcyChat} from './features/drcy-chat.ts';
import {initRepoMigration} from './features/repo-migration.ts';
import {initRepoProject} from './features/repo-projects.ts';
import {initTableSort} from './features/tablesort.ts';
import {initAdminUserListSearchForm} from './features/admin/users.ts';
import {initAdminConfigs} from './features/admin/config.ts';
import {initMarkupAnchors} from './markup/anchors.ts';
import {initNotificationCount, initNotificationsTable} from './features/notification.ts';
import {initRepoIssueContentHistory} from './features/repo-issue-content.ts';
import {initRepoIssueCommentFilter} from './features/repo-issue-comment-filter.ts';
import {initSelectionRegion} from './features/selectregion.ts';
import {initStopwatch} from './features/stopwatch.ts';
import {initFindFileInRepo} from './features/repo-findfile.ts';
import {initMarkupContent} from './markup/content.ts';
import {initPdfViewer} from './render/pdf.ts';
import {initUserAuthOauth2, initUserCheckAppUrl} from './features/user-auth.ts';
import {initRepoPullRequestAllowMaintainerEdit, initRepoPullRequestReview, initRepoIssueSidebarDependency, initRepoIssueDependencyDelete, initRepoIssueFilterItemLabel} from './features/repo-issue.ts';
import {initRepoEllipsisButton, initCommitStatuses} from './features/repo-commit.ts';
import {initRepoTopicBar} from './features/repo-home.ts';
import {initAdminCommon} from './features/admin/common.ts';
import {initRepoTemplateSearch} from './features/repo-template.ts';
import {initRepoCodeView} from './features/repo-code.ts';
import {initRepoSidebar} from './features/repo-sidebar.ts';
import {initSshKeyFormParser} from './features/sshkey-helper.ts';
import {initUserSettings} from './features/user-settings.ts';
import {initRepoActivityTopAuthorsChart, initRepoArchiveLinks} from './features/repo-common.ts';
import {initRepoMigrationStatusChecker} from './features/repo-migrate.ts';
import {initRepoDiffView} from './features/repo-diff.ts';
import {initOrgTeam, initOrgTeamMembers} from './features/org-team.ts';
import {initUserAuthWebAuthn, initUserAuthWebAuthnRegister} from './features/user-auth-webauthn.ts';
import {initRepoRelease, initRepoReleaseNew} from './features/repo-release.ts';
import {initRepoEditor} from './features/repo-editor.ts';
import {initCompSearchUserBox} from './features/comp/SearchUserBox.ts';
import {initInstall} from './features/install.ts';
import {initCompWebHookEditor} from './features/comp/WebHookEditor.ts';
import {initRepoBranchButton} from './features/repo-branch.ts';
import {initCommonOrganization} from './features/common-organization.ts';
import {initRepoWikiForm} from './features/repo-wiki.ts';
import {initRepository, initBranchSelectorTabs} from './features/repo-legacy.ts';
import {initCopyContent} from './features/copycontent.ts';
import {initCaptcha} from './features/captcha.ts';
import {initRepositoryActionView} from './components/init-repo-action-view.ts';
import {initGlobalTooltips} from './modules/tippy.ts';
import {initGiteaFomantic} from './modules/fomantic.ts';
import {initSubmitEventPolyfill, onDomReady} from './utils/dom.ts';
import {initRepoIssueList} from './features/repo-issue-list.ts';
import {initCommonIssueListQuickGoto} from './features/common-issue-list.ts';
import {initRepoContributors} from './features/contributors.ts';
import {initRepoCodeFrequency} from './features/code-frequency.ts';
import {initRepoRecentCommits} from './features/recent-commits.ts';
import {initRepoDiffCommitBranchesAndTags} from './features/repo-diff-commit.ts';
import {initGlobalSelectorObserver} from './modules/observer.ts';
import {initRepositorySearch} from './features/repo-search.ts';
import {initReviewFileComment, initReviewPageComment} from './features/review-comment.ts';
import {initColorPickers} from './features/colorpicker.ts';
import {initAdminSelfCheck} from './features/admin/selfcheck.ts';
import {initOAuth2SettingsDisableCheckbox} from './features/oauth2-settings.ts';
import {initGlobalFetchAction} from './features/common-fetch-action.ts';
import {initFootLanguageMenu, initGlobalAvatarUploader, initGlobalDropdown, initGlobalInput, initGlobalTabularMenu, initHeadNavbarContentToggle} from './features/common-page.ts';
import {initGlobalButtonClickOnEnter, initGlobalButtons, initGlobalDeleteButton} from './features/common-button.ts';
import {initGlobalComboMarkdownEditor, initGlobalEnterQuickSubmit, initGlobalFormDirtyLeaveConfirm} from './features/common-form.ts';
import {initAllspiceProjectViewSwitcher, initAllspiceProjectView} from './allspice_app/index.ts';
import {initIssueCommentHashChangeFixer} from './features/repo-issuecomment.ts';
import {initAllspiceAttachedAccordionToggle} from './features/allspice-attached-accordion-toggle.ts';
import {callInitFunctions} from './modules/init.ts';
import {initRepoViewFileTree} from './features/repo-view-file-tree.ts';

initGiteaFomantic();
initSubmitEventPolyfill();

initDependencyInjection();

onDomReady(() => {
  const initStartTime = performance.now();
  const initPerformanceTracer = callInitFunctions([
    initGlobalAvatarUploader,
    initGlobalDropdown,
    initGlobalTabularMenu,
    initGlobalFetchAction,

    // AllSpice initialization
    // Note: this is earlier in the init process because of the following Vue 2 -> Vue 3 change
    // https://v3-migration.vuejs.org/breaking-changes/mount-changes.html
    // This broke some of the Gitea JQuery functions since the DOM was modified after handlers were attached
    initSelectionRegion,
    initReviewFileComment,
    initReviewPageComment,
    initAllSpice,
    initAllspiceProjectView,
    initAllspiceProjectViewSwitcher,

    initAllspiceAttachedAccordionToggle,

    initGlobalTooltips,
    initGlobalButtonClickOnEnter,
    initGlobalButtons,
    initGlobalCopyToClipboardListener,
    initGlobalEnterQuickSubmit,
    initGlobalFormDirtyLeaveConfirm,
    initGlobalComboMarkdownEditor,
    initGlobalDeleteButton,
    initGlobalInput,

    initCommonOrganization,
    initCommonIssueListQuickGoto,

    initCompSearchUserBox,
    initCompWebHookEditor,

    initInstall,

    initHeadNavbarContentToggle,
    initFootLanguageMenu,
    initHeadwayTracking,

    initContextPopups,
    initHeatmap,
    initImageDiff,
    initSmartReviewSeveritySlider,
    initMarkupAnchors,
    initMarkupContent,
    initDrcyChat,
    initSshKeyFormParser,
    initStopwatch,
    initTableSort,
    initFindFileInRepo,
    initCopyContent,

    initAdminCommon,
    initAdminUserListSearchForm,
    initAdminConfigs,
    initAdminSelfCheck,

    initDashboardRepoList,

    initNotificationCount,
    initNotificationsTable,

    initOrgTeam,
    initOrgTeamMembers,

    initRepoActivityTopAuthorsChart,
    initRepoArchiveLinks,
    initRepoBranchButton,
    initRepoCodeView,
    initRepoSidebar,
    initBranchSelectorTabs,
    initRepoEllipsisButton,
    initRepoDiffCommitBranchesAndTags,
    initRepoEditor,
    initRepoGraphGit,
    initRepoIssueContentHistory,
    initRepoIssueCommentFilter,
    initRepoIssueList,
    initRepoIssueFilterItemLabel,
    initRepoIssueSidebarDependency,
    initRepoIssueDependencyDelete,
    initRepoMigration,
    initRepoMigrationStatusChecker,
    initRepoProject,
    initRepoPullRequestAllowMaintainerEdit,
    initRepoPullRequestReview,
    initRepoRelease,
    initRepoReleaseNew,
    initRepoTemplateSearch,
    initRepoTopicBar,
    initRepoViewFileTree,
    initRepoWikiForm,
    initRepository,
    initRepositoryActionView,
    initRepositorySearch,
    initRepoContributors,
    initRepoCodeFrequency,
    initRepoRecentCommits,
    initIssueCommentHashChangeFixer,

    initCommitStatuses,
    initCaptcha,

    initUserCheckAppUrl,
    initUserAuthOauth2,
    initUserAuthWebAuthn,
    initUserAuthWebAuthnRegister,
    initUserSettings,
    initRepoDiffView,
    initPdfViewer,
    initColorPickers,

    initOAuth2SettingsDisableCheckbox,
  ]);

  // it must be the last one, then the "querySelectorAll" only needs to be executed once for global init functions.
  initGlobalSelectorObserver(initPerformanceTracer);
  if (initPerformanceTracer) initPerformanceTracer.printResults();

  const initDur = performance.now() - initStartTime;
  if (initDur > 500) {
    console.error(`slow init functions took ${initDur.toFixed(3)}ms`);
  }
});
